import { Component, inject, OnInit, signal } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgGlyph } from "@ng-icons/core";
import { Router, RouterLink } from "@angular/router";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { ImageRotatorComponent } from "../../../../Common/Components/ImageRotator/ImageRotatorComponent";
import { AuthState } from "../../State/AuthState";
import { AuthEffects } from "../../State/AuthEffects";

@Component({
  standalone: true,
  selector: "app-login-page",
  imports: [ReactiveFormsModule, RouterLink, AppCardComponent, AppButtonComponent, ImageRotatorComponent, NgGlyph],
  templateUrl: "./LoginPage.html",
  styleUrl: "./LoginPage.scss",
})
export class LoginPage implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  public readonly authState = inject(AuthState);
  public readonly authEffects = inject(AuthEffects);
  public readonly isPasswordVisible = signal(false);

  form = this.formBuilder.group({
    email: ["", [Validators.required, Validators.email]],
    password: ["", [Validators.required]],
  });

  rotatorImages = [
    { src: "/Assets/Images/Modules/Auth/Login/Landing1.webp", alt: "The Mailbox Club activity 1" },
    { src: "/Assets/Images/Modules/Auth/Login/Landing2.webp", alt: "The Mailbox Club activity 2" },
    { src: "/Assets/Images/Modules/Auth/Login/Landing3.webp", alt: "The Mailbox Club activity 3" },
  ];

  async ngOnInit(): Promise<void> {
    // Redirect authenticated users away from login page
    if (this.authState.isAuthenticated()) {
      await this.router.navigate(["/home"]);
      return;
    }

    // Try to restore session and redirect if successful
    const restored = await this.authEffects.restoreSession();
    if (restored && this.authState.isAuthenticated()) {
      await this.router.navigate(["/home"]);
    }
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { email, password } = this.form.value;
    if (!email || !password) {
      this.form.markAllAsTouched();
      return;
    }

    await this.authEffects.login({ email, password });
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible.update((visible) => !visible);
  }
}
